import { Num, T, Var } from 'gt-react';
import type { UploadLayoutDefinition } from '@erp/rpc';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { InvalidResults } from './InvalidResults';
import { resultsToUpload } from './resultsToUpload';
import { columnIndex, type ResultsSheet } from './sheetCells';

/** Rows of the dropped file the preview converts, so typing stays instant. */
const PREVIEW_INPUT_ROWS = 50;
/** Upload lines the preview shows. */
const PREVIEW_LINES = 5;

interface Props {
  sheet: ResultsSheet | null;
  lookupSheet: ResultsSheet | null;
  definition: UploadLayoutDefinition | null;
}

/**
 * The draft layout run against the first rows of the dropped file: what the
 * upload would look like, or what stops it, before anything is saved.
 */
export function LayoutPreview({ sheet, lookupSheet, definition }: Props) {
  if (!sheet) {
    return (
      <p className="text-muted-foreground text-sm">
        <T context="upload layout preview when no file has been dropped">
          Drop a supplier file above to preview this layout on its first rows.
        </T>
      </p>
    );
  }
  if (!definition) {
    return (
      <p className="text-muted-foreground text-sm">
        <T context="upload layout preview while a mapping row is incomplete">
          Fix the highlighted mappings to see the preview.
        </T>
      </p>
    );
  }

  const result = resultsToUpload(
    { ...sheet, rows: sheet.rows.slice(0, PREVIEW_INPUT_ROWS) },
    definition,
    lookupSheet,
  );
  if (result.status === 'invalid') return <InvalidResults result={result} />;

  const columns = [...definition.columns]
    .map(({ column }) => column)
    .sort((a, b) => columnIndex(a) - columnIndex(b));
  const lines = result.lines.slice(0, PREVIEW_LINES);

  return (
    <div className="flex flex-col gap-1">
      <p className="text-muted-foreground text-xs">
        <T context="caption over the upload layout preview table">
          First <Num>{lines.length}</Num> upload lines from the first{' '}
          <Num>{PREVIEW_INPUT_ROWS}</Num> input rows (
          <Num>{result.skipped.length}</Num> skipped).
        </T>
      </p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <T context="upload layout preview column: Excel row of the input">
                  Input row
                </T>
              </TableHead>
              {columns.map((column) => (
                <TableHead key={column} className="font-mono">
                  {column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.sheetRow}>
                <TableCell className="text-muted-foreground">
                  <Var>{line.sheetRow}</Var>
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column}
                    className="font-mono text-xs whitespace-pre"
                  >
                    {line.cells[columnIndex(column)]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
